import { useCallback } from 'react';
import { Loading } from '../../components/Loading/Loading';
import { ErrorMessage } from '../../components/ErrorMessage/ErrorMessage';
import {
  StoryCardStandard,
  StoryCardCartoon,
  StoryCardHero,
  StoryCardHeadline,
  StoryCardColumnist,
  StoryCardOverlay,
  StoryCardAudio,
} from '../../components/StoryCard/StoryCard';
import { useApi } from '../../hooks/useApi';
import { fetchStoryFeed } from '../../lib/api';
import { byNewestFirst, mapStory } from '../../lib/stories';
import './FeedView.css';

// The special formats are the exception, not the rhythm: subtype and section
// pick the columnist, podcast and cartoon cards, position gives the lead story
// a hero, and one display format breaks up every FEATURE_EVERY standard cards.
// Everything else is standard.
const FEATURE_ROTATION = [StoryCardOverlay, StoryCardHeadline];
const FEATURE_EVERY = 4;

function pickCards(stories) {
  let sinceFeature = 0;
  let featureIndex = 0;

  return stories.map((story, index) => {
    if (story.subtype === 'columnist') {
      return { story, Card: StoryCardColumnist };
    }

    if (story.subtype === 'podcast') {
      return { story, Card: StoryCardAudio };
    }

    // The drawing is the whole story. With no image there is nothing to show,
    // so those fall through to the standard card.
    if (story.section.toLowerCase() === 'cartoon' && story.image) {
      return { story, Card: StoryCardCartoon };
    }

    if (index === 0) {
      return { story, Card: StoryCardHero };
    }

    // Only stories with art can carry a display format.
    if (sinceFeature >= FEATURE_EVERY && story.image) {
      sinceFeature = 0;
      const Card = FEATURE_ROTATION[featureIndex % FEATURE_ROTATION.length];
      featureIndex += 1;
      return { story, Card };
    }

    sinceFeature += 1;
    return { story, Card: StoryCardStandard };
  });
}

// A tab panel wrapping a story feed. `id` ties the panel to its tab's
// aria-controls; with the pager keeping several panels mounted, each needs
// its own.
export function FeedView({ id, title, query, layout = 'cards', emptyMessage }) {
  return (
    <section
      id={id}
      className="feed-view"
      role="tabpanel"
      aria-label={title}
    >
      <FeedContent query={query} layout={layout} emptyMessage={emptyMessage} />
    </section>
  );
}

// The feed itself, minus the panel wrapper, so views that add their own
// chrome around a feed (see FollowingView) can reuse it. `query` is a
// story-feed query string, or null when the user has not chosen anything to
// fill this feed with — in which case `emptyMessage` says so.
export function FeedContent({ query, layout = 'cards', emptyMessage }) {
  const fetchFeed = useCallback(
    () => (query ? fetchStoryFeed({ query, size: 15 }) : Promise.resolve(null)),
    [query],
  );
  const { data, isLoading, error } = useApi(fetchFeed);

  const stories = (data?.content_elements ?? []).map(mapStory);

  if (isLoading) return <Loading message="Fetching stories..." />;
  if (error) return <ErrorMessage message={error} />;
  if (!query) return <p className="feed-view__empty">{emptyMessage}</p>;

  if (stories.length === 0) {
    return <p className="feed-view__empty">No stories found for this feed.</p>;
  }

  return <FeedBody stories={stories} layout={layout} />;
}

function FeedBody({ stories, layout }) {
  if (layout === 'list') {
    return (
      <ul className="feed-view__stories">
        {stories.map((story) => (
          <li key={story.id} className="feed-view__story">
            {story.headline}
          </li>
        ))}
      </ul>
    );
  }

  // Following reads as a social feed rather than a front page: one card
  // format throughout, led by the byline, strictly newest first no matter
  // which of the followed columnists wrote it.
  if (layout === 'following') {
    return (
      <ul className="feed-view__cards feed-view__cards--following">
        {byNewestFirst(stories).map((story) => (
          <li key={story.id} className="feed-view__card">
            <StoryCardColumnist story={story} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul
      className={
        stories.every((s) => s.subtype === 'podcast')
          ? 'feed-view__cards feed-view__cards--audio'
          : 'feed-view__cards'
      }
    >
      {pickCards(stories).map(({ story, Card }) => (
        <li
          key={story.id}
          className={
            Card === StoryCardStandard
              ? 'feed-view__card feed-view__card--standard'
              : 'feed-view__card'
          }
        >
          <Card story={story} />
        </li>
      ))}
    </ul>
  );
}
